import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { AspectRatio, AspectRatioLabels, IUserModel, ModelType, getLabelsByLocale, Locale } from '@packages/global';
import { useModelStore } from '../../stores/modelStore';
import { useCoinStore } from '../../stores/coinStore';
import { useTranslation } from 'react-i18next';

const EPISODE_SCRIPT_PRICE = 20;

export interface CreateEpisodeConfig {
  text2imageModelId: string;
  llmModelId: string;
  aspectRatio: AspectRatio;
  prompt: string;
  targetDuration: number | null;
  maxShotDuration: number | null;
}

export interface CreateEpisodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: CreateEpisodeConfig) => void;
  isProcessing?: boolean;
  chapterTitle: string;
  defaultConfig?: Partial<CreateEpisodeConfig>;
}

// Default prompt template is now provided via i18n: project:createEpisode.defaultPrompt

/** 内联模型选择器 */
function InlineModelSelector({
  label,
  models,
  value,
  onChange,
  required = false,
  error = false,
}: {
  label: string;
  models: IUserModel[];
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
  error?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedModel = models.find(m => m.id === value);
  const { t } = useTranslation('project');

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2.5 bg-bg-elevated border rounded-lg hover:border-border-default transition-colors cursor-pointer ${error ? 'border-error' : 'border-border-subtle'
          }`}
      >
        <span className={`text-sm truncate ${selectedModel ? 'text-gray-200' : 'text-gray-400'}`}>
          {selectedModel?.title || t('createEpisode.selectPlaceholder')}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-bg-elevated border border-border-subtle rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
          {models.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">{t('createEpisode.noModels')}</div>
          ) : (
            models.map(model => (
              <button
                key={model.id}
                type="button"
                onClick={() => {
                  onChange(model.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-bg-surface transition-colors cursor-pointer ${model.id === value ? 'bg-bg-surface' : ''
                  }`}
              >
                <span className="text-sm text-gray-200 truncate">{model.title}</span>
                {model.id === value && <CheckIcon className="w-4 h-4 text-primary" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** 比例选择器 */
function AspectRatioButtons({
  value,
  onChange,
  error = false,
}: {
  value: AspectRatio;
  onChange: (ratio: AspectRatio) => void;
  error?: boolean;
}) {
  const { i18n } = useTranslation();
  const ratios = Object.entries(getLabelsByLocale(AspectRatioLabels, i18n.language as Locale)) as [AspectRatio, string][];

  return (
    <div className={`flex gap-2 flex-wrap p-1 rounded-lg ${error ? 'ring-1 ring-error' : ''}`}>
      {ratios.map(([ratio, label]) => (
        <button
          key={ratio}
          type="button"
          onClick={() => onChange(ratio)}
          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer ${value === ratio
            ? 'bg-primary/20 border-primary text-primary'
            : 'bg-bg-elevated border-border-subtle text-gray-300 hover:border-border-default'
            }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const DURATION_OPTIONS: (number | null)[] = [null, 15, 30, 60, 90, 120];
const MAX_SHOT_DURATION_OPTIONS: (number | null)[] = [null, 2.5, 5, 7.5, 10, 12.5, 15];

export default function CreateEpisodeDialog({
  isOpen,
  onClose,
  onConfirm,
  isProcessing = false,
  chapterTitle,
  defaultConfig,
}: CreateEpisodeDialogProps) {
  const [text2imageModelId, setText2imageModelId] = useState('');
  const [llmModelId, setLlmModelId] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(AspectRatio.LANDSCAPE);
  const [prompt, setPrompt] = useState('');
  const [targetDuration, setTargetDuration] = useState<number | null>(null);
  const [maxShotDuration, setMaxShotDuration] = useState<number | null>(null);
  const [errors, setErrors] = useState<{
    text2imageModelId?: boolean;
    llmModelId?: boolean;
    aspectRatio?: boolean;
  }>({});

  const { models, fetchModels, getModelsByType } = useModelStore();
  const { account } = useCoinStore();
  const { t } = useTranslation(['project', 'common']);

  const text2imageModels = getModelsByType(ModelType.TEXT_TO_IMAGE);
  const llmModels = getModelsByType(ModelType.LLM);

  const balance = account?.balance ?? 0;
  const hasEnoughCoins = balance >= EPISODE_SCRIPT_PRICE;

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  useEffect(() => {
    if (isOpen) {
      const currentText2imageModels = getModelsByType(ModelType.TEXT_TO_IMAGE);
      const currentLlmModels = getModelsByType(ModelType.LLM);
      setText2imageModelId(defaultConfig?.text2imageModelId || currentText2imageModels[0]?.id || '');
      setLlmModelId(defaultConfig?.llmModelId || currentLlmModels[0]?.id || '');
      setAspectRatio(defaultConfig?.aspectRatio || AspectRatio.LANDSCAPE);
      setPrompt(defaultConfig?.prompt || t('project:createEpisode.defaultPrompt', { chapterTitle }));
      setTargetDuration(defaultConfig?.targetDuration ?? null);
      setMaxShotDuration(defaultConfig?.maxShotDuration ?? null);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chapterTitle, defaultConfig, models.length]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    if (!text2imageModelId) newErrors.text2imageModelId = true;
    if (!llmModelId) newErrors.llmModelId = true;
    if (!aspectRatio) newErrors.aspectRatio = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validateForm()) return;
    onConfirm({ text2imageModelId, llmModelId, aspectRatio, prompt, targetDuration, maxShotDuration });
  };

  const isValid = text2imageModelId && llmModelId && aspectRatio;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-2xl bg-bg-page rounded-2xl border border-border-subtle shadow-2xl">
          {/* 头部 */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <SparklesIcon className="w-5 h-5 text-primary" />
              </div>
              <DialogTitle className="text-base font-semibold text-gray-50">
                {t('project:createEpisode.title')}
              </DialogTitle>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors cursor-pointer"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* 内容 */}
          <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-gray-300">
              {t('project:createEpisode.description', { title: chapterTitle })}
            </p>

            {/* 提示词 */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('project:createEpisode.prompt')}
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-bg-form border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-border-default resize-none transition-colors"
                placeholder={t('project:createEpisode.promptPlaceholder')}
              />
            </div>

            {/* 模型配置 */}
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('project:createEpisode.modelConfig')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <InlineModelSelector
                  label={t('project:createEpisode.text2imageModel')}
                  models={text2imageModels}
                  value={text2imageModelId}
                  onChange={setText2imageModelId}
                  required
                  error={errors.text2imageModelId}
                />
                <InlineModelSelector
                  label={t('project:createEpisode.llmModel')}
                  models={llmModels}
                  value={llmModelId}
                  onChange={setLlmModelId}
                  required
                  error={errors.llmModelId}
                />
              </div>
            </div>

            {/* 视频比例 */}
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('project:createEpisode.aspectRatio')} <span className="text-error">*</span>
              </label>
              <AspectRatioButtons
                value={aspectRatio}
                onChange={setAspectRatio}
                error={errors.aspectRatio}
              />
            </div>

            {/* 视频时长 */}
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('project:createEpisode.duration')}
              </label>
              <div className="flex gap-2 flex-wrap">
                {DURATION_OPTIONS.map((val) => (
                  <button
                    key={val ?? 'auto'}
                    type="button"
                    onClick={() => setTargetDuration(val)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer ${targetDuration === val
                      ? 'bg-amber-400/20 border-amber-400 text-amber-400'
                      : 'bg-bg-elevated border-border-subtle text-gray-300 hover:border-border-default'
                      }`}
                  >
                    {val === null ? t('project:createEpisode.durationAuto') : t('project:createEpisode.durationSeconds', { val })}
                  </button>
                ))}
              </div>
            </div>

            {/* 单镜最大时长 */}
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                {t('project:createEpisode.maxShotDurationLabel')}
              </label>
              <div className="flex gap-2 flex-wrap">
                {MAX_SHOT_DURATION_OPTIONS.map((val) => (
                  <button
                    key={val ?? 'no-limit'}
                    type="button"
                    onClick={() => setMaxShotDuration(val)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer ${maxShotDuration === val
                      ? 'bg-amber-400/20 border-amber-400 text-amber-400'
                      : 'bg-bg-elevated border-border-subtle text-gray-300 hover:border-border-default'
                      }`}
                  >
                    {val === null ? t('project:createEpisode.maxShotDurationNoLimit') : t('project:createEpisode.maxShotDurationSeconds', { val })}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex gap-3 p-4 border-t border-border-subtle">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-bg-elevated hover:bg-bg-surface rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              {t('common:cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isProcessing || !isValid || !hasEnoughCoins}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              {isProcessing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('project:createEpisode.processing')}
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4" />
                  {t('project:createEpisode.confirm')}
                  <span className={`text-xs font-normal ${hasEnoughCoins ? 'text-yellow-400' : 'text-red-400'}`}>
                    ({EPISODE_SCRIPT_PRICE} {t('project:createEpisode.coins')})
                  </span>
                </>
              )}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
